import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics34() {

  const contentRef = useRef(null);
         const links = {
        prev: "/economics-33", 
        index: "/index-economics",                 
        next: "/economics-35", 
      };
        const titleText ="Parity Pricing: A Zero-Sum Fallacy";
        const questions = [
  {
    text: "1. What was the core idea behind the 1933 Price Parity bill in the United States?",
    options: [
      { value: "A", label: "A. To limit industrial production and reduce inflation" },
      {
        value: "B",
        label:
          "B. To peg the prices of farm products to a past era of agricultural prosperity",
      },
      {
        value: "C",
        label:
          "C. To encourage free market practices in the agricultural sector",
      },
      {
        value: "D",
        label:
          "D. To increase imports of industrial equipment for farmers",
      },
    ],
    correct: "B",
  },
  {
    text: "2. Which of the following is a key criticism of government-imposed price parity?",
    options: [
      { value: "A", label: "A. It promotes healthy competition among farmers" },
      { value: "B", label: "B. It ensures long-term stability in food prices" },
      {
        value: "C",
        label:
          "C. It creates artificial scarcity and distorts market signals",
      },
      { value: "D", label: "D. It reduces the cost of living in urban areas" },
    ],
    correct: "C",
  },
  {
    text: "3. According to the lesson, how does price parity affect urban consumers?",
    options: [
      { value: "A", label: "A. They benefit by getting cheaper farm produce" },
      {
        value: "B",
        label:
          "B. They pay higher prices, reducing their overall purchasing power",
      },
      { value: "C", label: "C. They gain access to a larger variety of products" },
      {
        value: "D",
        label:
          "D. Their income increases in proportion to farm income",
      },
    ],
    correct: "B",
  },
  {
    text: "4. What does the argument for price parity based on industrial tariffs ignore?",
    options: [
      { value: "A", label: "A. That tariffs no longer exist in modern economies" },
      { value: "B", label: "B. That farmers are immune to market forces" },
      {
        value: "C",
        label:
          "C. That consumers end up being penalized twice, through tariffs and parity prices",
      },
      {
        value: "D",
        label:
          "D. That industrial workers benefit more from subsidies",
      },
    ],
    correct: "C",
  },
];

 // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);

  return (
    <div className="container" ref={contentRef  }>
    <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={34}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />
     

      <p className="content">
        Welcome to <b>Day 34</b>! Today, we turn our attention to a commonly
        misunderstood and politically popular policy mechanism known as price
        parity. Introduced as a corrective tool to support certain economic
        sectors, price parity has often been promoted as a way to help farmers
        achieve economic balance with the industrial sector. Though seemingly
        well-intentioned, this mechanism is based on flawed reasoning and leads
        to distortions that end up harming the very economy it aims to uplift.
        In today’s session, we will explore the historical roots of price parity
        policies, the economic realities they ignore, the harmful consequences
        they entail, and the fallacy of the fairness argument used to defend
        them. By the end of this session, learners will gain a clearer
        understanding of how such seemingly beneficial interventions can result
        in hidden losses and inefficiencies across the broader economic
        landscape.
        <br />
        <br />
        <b>The Historical Origins and Logic of Price Parity</b>
        <br />
        <br />
        One of the most striking instances of price parity intervention was the
        legislation passed in the United States in 1933. This policy pegged the
        prices of farm products to those of industrial goods based on the
        relative prices during the 1909-1914 period. The rationale was that,
        during this timeframe, farming was experiencing an economic boom, and
        the farm sector was relatively prosperous. Politicians and economic
        planners believed that if farmers could sell their goods at the same
        price levels as they had during that era, they would again enjoy similar
        prosperity. The motivation behind this policy was not entirely
        misplaced: farming incomes had taken a hit, and a political consensus
        emerged that something had to be done to preserve the livelihoods of
        farmers. Advocates claimed that ensuring farmers' prosperity would also
        ensure national prosperity, given the interlinkage of food production
        with national wellbeing. This logic, however, made the fatal mistake of
        treating the economy as a static system, where variables and conditions
        can be held constant over time.
        <br />
        <br />
        <b>Economic Realities Ignored by Parity Schemes</b>
        <br />
        <br />
        The most glaring flaw in the parity pricing scheme lies in its
        assumption that production costs, consumer behavior, and economic
        structures remain unchanged over time. The reality, however, is that the
        cost of producing agricultural goods evolves with technological
        advancements. The use of fertilizers, pesticides, genetically modified
        seeds, advanced irrigation methods, and farming machinery has
        significantly brought down the cost of agricultural production. Modern
        methods reduce waste, enhance efficiency, and increase overall yield.
        Therefore, expecting that prices of farm produce should mirror those of
        a bygone era ignores the fact that the inputs and mechanisms have
        changed radically. Moreover, prosperity in farming today does not come
        from higher nominal prices but from efficient and abundant production
        made possible through industrial progress. If industrial growth leads to
        higher wages and greater purchasing power in urban areas, farmers
        automatically benefit from higher demand and better prices. However,
        artificially enforcing price parity distorts this natural flow of
        economic benefits and undermines efficiency by setting prices that are
        no longer reflective of current realities.
        <br />
        <br />
        <b>Economic Losses Hidden Behind Political Gains</b>
        <br />
        <br />
        Supporters of price parity often fail to see the broader consequences of
        enforcing such policies. When the government enforces higher prices for
        farm produce either through direct price support, by purchasing crops at
        elevated prices, or by lending money to farmers to delay their produce
        from reaching markets it might appear that farmers are benefiting.
        However, this benefit comes at a steep cost to the rest of the economy.
        The urban consumer, for instance, now has to pay more for essential
        items like food, leaving them with less disposable income for other
        goods and services. The net result is not increased prosperity, but a
        zero-sum transfer of purchasing power from one sector to another. Worse
        still, mechanisms used to enforce price parity often lead to outright
        wastage. Crops may be destroyed or held back to create artificial
        scarcity, leading to lost economic value. Additionally, policies that
        restrict land use to drive up prices further misallocate resources. And
        in many cases, these interventions fail to make farmers genuinely better
        off. If farmers are forced to produce less to maintain artificially high
        prices, their total income may remain the same, thereby nullifying the
        purpose of the policy. When that happens, the discussion shifts to
        demanding "income parity," further compounding the economic burden on
        other sectors.
        <br />
        <br />
        <b>The Illusion of Fairness and Its Double Burden</b>
        <br />
        <br />
        One final line of defense for parity policies is the claim that since
        farmers are forced to pay higher prices for industrial goods due to
        tariffs, they deserve compensatory pricing for their own goods. This
        argument introduces a dangerous tit-for-tat logic in economic
        policymaking. Even if we were to momentarily accept this premise, it
        still does not hold up to scrutiny. The average consumer in the economy,
        who buys both industrial and farm products, ends up bearing the brunt of
        both policies. They pay higher prices for manufactured goods due to
        tariffs and are again penalized through inflated farm prices because of
        parity laws. Thus, instead of correcting an imbalance, the policy
        compounds the problem by making everyday life more expensive for the
        common man. Moreover, it is impossible to craft a system where every
        participant subjected to tariffs is perfectly compensated through
        subsidies or parity pricing. Such a model is unsustainable and riddled
        with inefficiencies. In practice, it leads to rent-seeking behavior,
        bureaucratic overheads, and increased distortions in economic signals,
        thereby harming innovation, entrepreneurship, and long-term growth. The
        attempt to engineer "fairness" through price parity ultimately results
        in economic imbalance, inefficiency, and a double burden on the general
        population.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b><br/><br/>
        In today’s lesson, we dissected the widely misunderstood concept of
        price parity. While the policy may appear to champion the cause of
        farmers and balance economic disparities, it is based on flawed
        assumptions and outdated data. We explored how policies like the 1933 US
        parity law pegged farm prices to historical industrial prices without
        considering evolving production costs and changing economic realities.
        We saw how such interventions result in zero-sum outcomes at best and
        economic losses at worst, due to artificial scarcity, resource
        misallocation, and decreased consumer purchasing power. Finally, we
        examined the fairness argument and concluded that compensating one group
        at the expense of another leads to greater injustice and inefficiency.
        True economic prosperity is driven by productivity and efficiency, not
        by politically manipulated price controls.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        As we conclude our exploration of price parity and sector-specific
        government interventions, Day 35 will dive deeper into the broader
        problem of artificially keeping entire industries alive. You'll examine
        how policies aimed at sustaining certain industries whether through
        subsidies, tariffs, quotas, or credit schemes often distort the natural
        flow of capital and labor. These interventions are usually justified
        with claims of national importance or economic collapse, but in reality,
        they often transfer wealth from efficient sectors to inefficient ones,
        reducing overall productivity. Tomorrow’s lesson will highlight why
        allowing uncompetitive industries to phase out naturally is essential
        for healthy economic growth and innovation.
      </p>
      <h2>Test your understanding</h2>
     {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics34;
